import { useEffect, useRef, useState } from 'react';
import { useMotionValue, useTransform, useSpring, useInView } from 'framer-motion';
import * as motion from 'framer-motion/client';
import css from './partner.module.scss';
import T from '../../../common/components/T';
import { navigateV2 } from '../../../common/utils/router';
import Image from 'next/image';

const BecomePartnerSection = ({ isUserToken, width }) => {
    const scrollY = useMotionValue(0);
    // Отдельное значение специально для маски/скейла картинки (самая дорогая
    // часть рендера в Safari из-за feTurbulence/feDisplacementMap) — обновляется
    // реже, чем scrollY, чтобы не проседал общий кадр вместе с текстом ниже.
    const revealScrollY = useMotionValue(0);
    const blockRef = useRef();
    const isInView = useInView(blockRef);

    const svgRef = useRef();
    const readyRef = useRef();
    const spanRef = useRef();

    // В Safari живой SVG-фильтр (feTurbulence/feDisplacementMap) на анимированной
    // маске — самая дорогая часть рендера этого блока и заметно лагает. В остальных
    // браузерах эта операция дешёвая, поэтому отключаем волнистый край только в Safari,
    // оставляя обычный ровный круг — сам ревил (радиус/скейл) не меняется.
    const [isSafari, setIsSafari] = useState(false);

    useEffect(() => {
        const ua = window.navigator.userAgent;
        setIsSafari(/^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua));
    }, []);

    // Радиус круга изменяется от 100 до 770
    const radius = useSpring(useTransform(revealScrollY, [-600, 80], [0, 770]), {
        stiffness: 200,
        damping: 220
    });

    // Масштаб изображения изменяется от 1 до 1.5
    const scale = useSpring(useTransform(revealScrollY, [-600, 80], [1, 1.5]), {
        stiffness: 200,
        damping: 220
    });

    const transformY = useSpring(
        useTransform(scrollY, [-350, 110], [-svgRef?.current?.getBoundingClientRect()?.height * 0.75 || -400, 0]),
        {
            stiffness: 100,
            damping: 20
        }
    );

    const transformX = useSpring(
        useTransform(scrollY, [-350, 110], [width / 2 - readyRef?.current?.getBoundingClientRect()?.width / 2 || 200, 0]),
        {
            stiffness: 100,
            damping: 20
        }
    );
    const transformY2 = useSpring(
        useTransform(scrollY, [-180, 110], [0, spanRef?.current?.getBoundingClientRect()?.height - 16 || -70]),
        {
            stiffness: 100,
            damping: 20
        }
    );

    const transformX2 = useSpring(
        useTransform(scrollY, [-180, 110], [0, -spanRef?.current?.getBoundingClientRect()?.width - 16 || -300]),
        {
            stiffness: 100,
            damping: 20
        }
    );

    const transformY3 = useSpring(useTransform(scrollY, [120, 180], [300, 0]), {
        stiffness: 100,
        damping: 20
    });

    const opacity = useSpring(useTransform(scrollY, [120, 180], [0, 1]), {
        stiffness: 100,
        damping: 20
    });

    useEffect(() => {
        const element = document.getElementById('animated-svg');
        if (!element) {
            return;
        }

        // Кэшируем позицию/высоту блока, чтобы не дёргать getBoundingClientRect()
        // на каждом событии scroll (форсированный синхронный layout — основная причина
        // подвисаний в Safari). Пересчитываем только при ресайзе/изменении ширины
        // и при повторном появлении блока во вьюпорте.
        let offsetTop = 0;
        let elementHeight = 0;

        const measure = () => {
            const rect = element.getBoundingClientRect();
            offsetTop = rect.top + window.scrollY;
            elementHeight = rect.height;
        };

        measure();

        const getValue = () => {
            if (width < 1400) {
                const v = (1400 - width) / 2.5;
                return window.scrollY - offsetTop + v;
            }
            return window.scrollY - offsetTop;
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    measure();
                    const value = window.scrollY - offsetTop;
                    scrollY.set(value);
                    revealScrollY.set(value);
                }
            },
            { threshold: 0 } // Срабатывает на любом пересечении элемента
        );

        observer.observe(element);

        let rafId = null;
        let debounceId = null;
        let lastRevealUpdate = 0;
        // Маску/скейл картинки (самый дорогой рендер в Safari) обновляем не
        // чаще, чем раз в ~100мс (~10 раз/сек) — это заметно реже, чем остальные
        // transform/opacity, которые остаются на полной частоте скролла.
        const REVEAL_UPDATE_INTERVAL = 100;

        const applyScroll = () => {
            rafId = null;
            const top = offsetTop - window.scrollY;
            const bottom = top + elementHeight;
            // Обновляем scrollY только если элемент находится в пределах области просмотра или близко к ней
            if (bottom >= 0 && top <= window.innerHeight) {
                const value = getValue();
                scrollY.set(value);

                const now = performance.now();
                if (now - lastRevealUpdate >= REVEAL_UPDATE_INTERVAL) {
                    lastRevealUpdate = now;
                    revealScrollY.set(value);
                }
            }
        };

        const handleScroll = () => {
            // Батчим обработку скролла к кадру рендера вместо того, чтобы
            // выполнять расчёт синхронно на каждое отдельное событие scroll.
            if (rafId === null) {
                rafId = requestAnimationFrame(applyScroll);
            }

            // Как только скролл остановился — досчитываем revealScrollY точно,
            // без троттлинга, чтобы не оставалось небольшого расхождения
            // с scrollY в состоянии покоя.
            if (debounceId !== null) {
                clearTimeout(debounceId);
            }
            debounceId = setTimeout(() => {
                lastRevealUpdate = performance.now();
                revealScrollY.set(getValue());
            }, 150);
        };

        const handleResize = () => measure();

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            observer.unobserve(element);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            if (debounceId !== null) {
                clearTimeout(debounceId);
            }
        };
    }, [scrollY, revealScrollY, width]);

    return (
        <div ref={blockRef} className={css.wrapper}>
            <motion.h2
                initial={{ y: '120%' }}
                animate={!isInView ? {} : { y: 0 }}
                transition={{ delay: 0, duration: 0.4, ease: 'linear' }}
                className={css.partnerTitleStatic}
            >
                <T textName={'landing-main_globaltrend_text'} defaultStr={'Global Trend'} page='/' />
            </motion.h2>
            <div
                ref={svgRef}
                id='animated-svg'
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                    willChange: 'transform',
                    isolation: 'isolate'
                }}
            >
                {width > 768 ? (
                    <svg width='1400' height='560' version='1.1' viewBox='0 0 1400 560' style={{ width: '100%', height: 'auto' }}>
                        <defs>
                            <filter
                                id='displacementFilter7'
                                filterUnits='userSpaceOnUse'
                                x={-150}
                                y={-150}
                                width={1700}
                                height={860}
                                // Считаем фильтр в разрешении, привязанном к CSS-пикселям
                                // региона (1700x860), а не к физическим пикселям экрана —
                                // на Retina (2x/3x) Safari иначе растеризует software-фильтр
                                // в 4-9 раз больше пикселей, чем реально видно на экране.
                                // Итоговый результат браузер просто масштабирует обратно.
                                filterRes='1700 860'
                            >
                                <feTurbulence type='fractalNoise' baseFrequency='0.03' numOctaves='1' result='noise' />
                                <feDisplacementMap
                                    in='SourceGraphic'
                                    in2='noise'
                                    scale='120'
                                    xChannelSelector='R'
                                    yChannelSelector='G'
                                />
                            </filter>
                            <mask id='circleMask7'>
                                <motion.circle
                                    // key форсирует полное пересоздание DOM-узла при смене isSafari —
                                    // framer-motion применяет статичный style.filter только при монтировании
                                    // элемента и не подхватывает его изменение на обычном ре-рендере.
                                    key={isSafari ? 'safari' : 'default'}
                                    cx='50%'
                                    cy='50%'
                                    r={radius}
                                    fill='white'
                                    style={{ filter: isSafari ? 'none' : 'url(#displacementFilter7)' }}
                                />
                            </mask>
                        </defs>
                        <motion.image
                            xlinkHref={'/src/landing/partner/partner_new.jpg'}
                            width='1400'
                            height='560'
                            mask='url(#circleMask7)'
                            style={{
                                transformOrigin: 'center center',
                                scale: scale
                            }}
                        />
                    </svg>
                ) : (
                    <div className={css.imageMobile}>
                        <Image fill src={'/src/landing/partner/partner-mobile.jpg'} alt='Become a partner' />
                    </div>
                )}
            </div>
            <div className={css.bottomBlockWrapper}>
                <motion.div
                    initial={{ y: '-100%' }}
                    animate={{ y: 0 }}
                    transition={{ delay: 0.75, duration: 1, ease: 'linear' }}
                    className={css.ellips1Wrapper}
                >
                    <Image src={'ellipse.svg'} alt='ellipse icon2' width={220} height={220} />
                </motion.div>
                <motion.div
                    initial={{ y: '-130%', x: '50%' }}
                    animate={{ y: 0, x: 0 }}
                    transition={{ delay: 0.75, duration: 1, ease: 'linear' }}
                    className={css.ellips2Wrapper}
                >
                    <Image src={'ellipse.svg'} alt='ellipse icon3' width={220} height={220} />
                </motion.div>
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    transition={{ delay: 0.75, duration: 1, ease: 'linear' }}
                    className={css.ellips3Wrapper}
                >
                    <Image src={'ellipse.svg'} alt='ellipse icon4' width={268} height={268} />
                </motion.div>
                <motion.div
                    initial={{ x: '20%', y: '140%' }}
                    animate={{ x: 0, y: 0 }}
                    transition={{ delay: 0.75, duration: 1, ease: 'linear' }}
                    className={css.ellips4Wrapper}
                >
                    <Image src={'ellipse.svg'} alt='ellipse icon5' width={377} height={377} />
                </motion.div>
                <div className={css.bottomBlock}>
                    <motion.p
                        className={css.readyToChange}
                        style={{
                            translateY: transformY,
                            translateX: transformX
                        }}
                        ref={readyRef}
                    >
                        <span ref={spanRef}>
                            <T textName={'landing-parterpage-new27112'} defaultStr={'Готовы к'} page='/' />
                        </span>
                        <motion.span style={{ translateY: transformY2, translateX: transformX2 }}>
                            <T textName={'landing-parterpage-new27113'} defaultStr={'переменам?'} page='/' />
                        </motion.span>
                    </motion.p>
                    <div className={css.rightBlock}>
                        <motion.p style={{ translateY: transformY3, opacity: opacity }} className={css.partnerDescriptionRight}>
                            <T
                                textName={'landing-partnerpage-new217'}
                                defaultStr={
                                    'Не упустите свой шанс стать частью Global Trend. Зарегистрируйтесь прямо сейчас и начните свой путь к успеху, здоровью и красоте!'
                                }
                                page='/'
                            />
                        </motion.p>
                        <motion.div className={css.buttonBlock} style={{ translateY: transformY3, opacity: opacity }}>
                            <p
                                className={css.carouselButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isUserToken) {
                                        navigateV2('/office');
                                    } else {
                                        navigateV2('/auth/register');
                                    }
                                }}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    if (isUserToken) {
                                        window.open('/office', '_blank');
                                    } else {
                                        window.open('/auth/register', '_blank');
                                    }
                                }}
                            >
                                <T textName={'landing-carouselpage-new26'} defaultStr={'Стать партнером'} page='/' />
                            </p>
                        </motion.div>
                    </div>
                </div>
            </div>
            <motion.h1 className={css.heroText} style={{ translateY: transformY3, opacity: opacity }}>
                <T textName={'landing-main_globaltrend_text'} defaultStr={'Global Trend'} page='/' />
            </motion.h1>
        </div>
    );
};
export default BecomePartnerSection;
